﻿import * as _ from 'lodash';
import * as hash from 'object-hash';
import { Component, FormEvent } from 'react';
import { connect, Dispatch } from 'react-redux';
import { AppActionCreators, BillingActionCreators, CenterActionCreators, IApplicationState, IAppState, ICenterAdminDataResponse, IValidateAchRoutingNumberResponse, OrganPackaging } from '@nkr/store/index';
import { Col, ControlLabel, Form, FormControl, FormGroup, InputGroup, Label, Panel, Row, Well } from 'react-bootstrap';
import { IAdminData, IBaseResponse, IDocumentsVersion, IListItem, IContact, IUpdateAdminDataRequest } from '@nkr/models/index';
import { IAdminDataValidation } from './validation'
import { CenterOrganType, DonorShieldStatus, OrganType, VersionedFileEntityType, YesNo } from '@nkr/enums/index';
import { DataType } from '../../data-type';
import { IErrorItem } from '../../error-item';
import { initialAdminData, initialAdminValidationData } from '../initial-data';
import { profileNavKey } from '../../profile-nav-key';
import { Validate } from '../../validate';
import { toast } from 'react-toastify';
import { ITextItem } from './text-item';
import { IFormItem, IFormItemRow } from './form-item';
import { IToggleItem } from './toggle-item';
import { downloadFile } from '@nkr/services/download-file';
import ReactHtmlParser from 'react-html-parser';
import UploadFile, { NewFileDisplayType, UploadFileType } from '../../upload-file';
import CenterProfileNav from '../edit-nav';
import MapButton from './map-button';
import PerFieldReasonModal, { ITrackedChange } from '../per-field-reason-modal';
import ActivityLogModal from '../activity-log-modal';
import { ActivityLogActionCreators, IActivityLogFilters, IActivityLogTrackedField, IGetTrackedFieldsResponse } from '@nkr/store/center/actions-center-activity-log';

interface IProps {
    centerId: number,
    isAdmin: boolean,
    isAdminUser: boolean,
    hasDataChanges: boolean,
    staffList: IContact[],
    micrositeCoachList: IListItem[],
    documentVersionList: IDocumentsVersion[],
    onTabClick: (href: string) => void,
    onDataChanged: (hasDataChanged: boolean) => void,
    updateErrors: (list: IErrorItem[]) => void
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getData: (centerId: number) => Promise<ICenterAdminDataResponse>,
    updateData: (centerId: number, request: IUpdateAdminDataRequest) => Promise<ICenterAdminDataResponse>,
    getVersionedFile: (centerId: number, id: number) => Promise<any>,
    validateAchRoutingNumber: (achRoutingNumber: string) => Promise<IValidateAchRoutingNumberResponse>,
    getActivityLog: (centerId: number, filters: IActivityLogFilters) => Promise<any>,
    getActivityLogTrackedFields: () => Promise<IGetTrackedFieldsResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    data: IAdminData,
    originalData: IAdminData,
    validation: IAdminDataValidation,
    hash: string,
    isDirty: boolean,
    isValid: boolean,
    trackedFields: IActivityLogTrackedField[],
    pendingTrackedChanges: ITrackedChange[],
    showPerFieldReason: boolean,
    showActivityLog: boolean
}

class CenterAdminTab extends Component<TProps, IState> {
    readonly state: IState = {
        data: initialAdminData,
        originalData: initialAdminData,
        validation: initialAdminValidationData,
        hash: '',
        isDirty: false,
        isValid: true,
        trackedFields: [],
        pendingTrackedChanges: [],
        showPerFieldReason: false,
        showActivityLog: false
    }

    componentDidMount = () => {
        this.getData();
        this.props.getActivityLogTrackedFields().then(response => {
            if (response.data.success) {
                this.setState({ trackedFields: response.data.results });
            }
        });
    }

    getData = () => {
        this.props.showLoading('Getting admin data, one moment please...').then(() => {
            this.props.getData(this.props.centerId)
                .then(response => {
                    this.setState({
                        data: response.data.results,
                        originalData: response.data.results,
                        hash: hash(response.data.results)
                    });
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    getLists = () => {
        let validation = this.state.validation;

        let yesNo = [{ label: 'Yes', value: 1 }, { label: 'No', value: 0 }];
        let yesNoUnknown = [{ label: 'Yes', value: 1 }, { label: 'No', value: 0 }, { label: 'Unknown', value: -1 }];
        let yesNoPilot = [{ label: 'Yes', value: 1 }, { label: 'No', value: 0 }, { label: 'Pilot', value: -1 }];
        let onOffPilot = [{ label: 'On', value: 2 }, { label: 'Off', value: 0 }, { label: 'Pilot', value: 1 }];
        let centerOrganType = [{ label: 'Kidney', value: CenterOrganType.Kidney }, { label: 'Liver', value: CenterOrganType.Liver }, { label: 'Both', value: CenterOrganType.Both }, { label: 'None', value: null },];

        let staffList = _.sortBy(this.props.staffList, item => item.fullName);
        let micrositeCoachList = _.sortBy(this.props.micrositeCoachList, item => item.description);

        let hasLiverDash = this.state.data.dashType == CenterOrganType.Liver || this.state.data.dashType == CenterOrganType.Both;
        let isDonorShieldDirectLiver = this.state.data.isDonorShieldDirectLiverCenter == YesNo.Yes;
        let showLiverDonorShieldAR = hasLiverDash && isDonorShieldDirectLiver;

        micrositeCoachList = [
            { id: 0, description: '- select -' },
            ...micrositeCoachList
        ];

        let isNkr = this.props.app.site.organType == OrganType.Kidney;

        let nonToggleList: ITextItem[] = [
            { property: 'alias', value: this.state.data.alias, label: 'Center Alias/Code:', validation: true, valid: validation.alias, type: DataType.String, display: true },
            { property: 'dashDomain', value: this.state.data.dashDomain, label: <><span>Dash Domain:</span><br /><span style={{ paddingLeft: 5, fontSize: 10 }}>(e.g. 'cornell.donorscreen.org')</span></>, validation: false, valid: null, type: DataType.String, display: true },
            { property: 'userName', value: this.state.data.userName, label: 'Username:', validation: true, valid: true, type: DataType.String, display: true },
            { property: 'password', value: this.state.data.password, label: 'Password:', validation: true, valid: true, type: DataType.Password, display: true },
            { property: 'billingModel', value: this.state.data.billingModel, label: 'Billing Model:', validation: false, valid: true, type: DataType.Select, values: [{ id: '0', description: 'Standard' }, { id: '1', description: 'Government Hospital' }], display: true },
            { property: 'monthlyFee', value: this.state.data.monthlyFee, label: <><Label bsStyle="info">If not qualified "Equal Access"</Label> Monthly Fee:</>, validation: false, type: DataType.Fee, display: true },
            { property: 'monthlyFeeNotes', value: this.state.data.monthlyFeeNotes, label: 'Monthly Fee Notes:', validation: false, type: DataType.String, display: true },
            { property: 'dashFee', value: this.state.data.dashFee, label: 'Monthly DASH Fee:', validation: false, type: DataType.Fee, display: true },
            { property: 'dashNotes', value: this.state.data.dashNotes, label: 'DASH Billing Notes:', validation: false, type: DataType.String, display: true },

            { property: 'achRoutingNumber', value: this.state.data.achRoutingNumber, label: 'ABA Routing Number:', validation: false, type: DataType.String, display: true },
            { property: 'achAccountNumber', value: this.state.data.achAccountNumber, label: 'Bank Account Number:', validation: false, type: DataType.String, display: true },

            { property: 'packagingAchRoutingNumber', value: this.state.data.packagingAchRoutingNumber, label: 'Routing Number:', validation: true, valid: validation.packagingRoutingNumber, type: DataType.String, display: true },
            { property: 'packagingAchAccountNumber', value: this.state.data.packagingAchAccountNumber, label: 'Account Number:', validation: false, type: DataType.String, display: true },

            { property: 'donorShieldFee', value: this.state.data.donorShieldFee, label: 'Donor Shield Fee:', validation: false, type: DataType.Fee, display: this.state.data.isInDonorShield == DonorShieldStatus.On },
            { property: 'liverDonorShieldFee', value: this.state.data.liverDonorShieldFee, label: 'Liver Donor Shield Fee:', validation: false, type: DataType.Fee, display: this.state.data.isInDonorShield == DonorShieldStatus.On },

            { property: 'transplantsCompleted', value: this.state.data.transplantsCompleted, label: 'Total NKR TXPs:', validation: false, type: DataType.Callout, display: true },

            { property: 'invoicePoNumber', value: this.state.data.invoicePoNumber, label: 'PO Number:', validation: false, type: DataType.String, display: true },
            { property: 'invoicePoStartDate', value: this.state.data.invoicePoStartDate, label: 'Start Date:', validation: false, type: DataType.Date, display: true },
            { property: 'invoicePoExpirationDate', value: this.state.data.invoicePoExpirationDate, label: 'Expiration Date:', validation: false, type: DataType.Date, display: true },
            { property: 'poAmount', value: this.state.data.poAmount, label: 'PO Limit Amount:', validation: false, type: DataType.Fee, display: true },
            { property: 'poBalance', value: this.state.data.poBalance, label: 'PO Balance:', validation: false, type: DataType.Fee, display: true },
            { property: 'invoiceDaysToRemit', value: this.state.data.invoiceDaysToRemit, label: 'Remittance Days:', validation: false, type: DataType.String, display: true },

            { property: 'nkrLiaisonId', value: this.state.data.nkrLiaisonId, label: 'NKR Liaison:', validation: false, type: DataType.SelectInteger, display: true, values: staffList.filter(x => x.isLiaison).map(x => { return { id: x.id, description: x.fullName }; }) },
            { property: 'executiveSponsorId', value: this.state.data.executiveSponsorId, label: 'Executive Sponsor:', validation: false, type: DataType.SelectInteger, display: true, values: staffList.filter(x => x.isLiaison).map(x => { return { id: x.id, description: x.fullName }; }) },
            { property: 'defaultMicrositeCoachUserId', value: this.state.data.defaultMicrositeCoachUserId, label: 'Default Microsite Coach:', validation: false, type: DataType.SelectInteger, display: true, values: micrositeCoachList },

            { property: 'contracts', value: 1, label: '', validation: false, type: DataType.Modal, display: true },
            { property: 'space', value: 1, label: '', validation: false, type: DataType.Space, display: true },
            { property: 'latitude', value: this.state.data.latitude, label: 'Latitude:', validation: false, type: DataType.Float, display: true },
            { property: 'longitude', value: this.state.data.longitude, label: 'Longitude:', validation: false, type: DataType.Float, display: true },
            { property: 'geoCheck', value: <MapButton latitude={this.state.data.latitude} longitude={this.state.data.longitude} />, label: '', validation: false, type: DataType.Component, display: true },

        ];

        let toggleList: IToggleItem[] = [
            { property: 'isInactivityFeeWaived', type: DataType.ToggleYesNo, value: this.state.data.isInactivityFeeWaived, label: 'Is Inactivity Fee Waived?', values: yesNo },
            { property: 'rmcrIsContractFeeWaived', type: DataType.ToggleYesNo, value: this.state.data.rmcrIsContractFeeWaived, label: 'Is RMCR Contract Fee Waived?', values: yesNo },
            { property: 'overrideDonorsProtectedKidney', type: DataType.ToggleYesNo, value: this.state.data.overrideDonorsProtectedKidney, label: 'Override DS Credit Req. for % Donors Protected (Kidney)?', values: yesNo },
            { property: 'overrideDonorsProtectedLiver', type: DataType.ToggleYesNo, value: this.state.data.overrideDonorsProtectedLiver, label: 'Override DS Credit Req. for % Donors Protected (Liver)?', values: yesNo },
            { property: 'isDeleted', type: DataType.ToggleYesNo, value: this.state.data.isDeleted, label: 'Is Center Deleted?', values: yesNo },
            { property: 'isTestCenter', type: DataType.ToggleYesNo, value: this.state.data.isTestCenter, label: 'Is Test Center?', values: yesNo },
            { property: 'memberCenterType', type: DataType.ToggleOrganType, value: this.state.data.memberCenterType, label: 'Member Center Type?', values: centerOrganType },
            { property: 'isSuspended', type: DataType.ToggleYesNo, value: this.state.data.isSuspended, label: 'Is Suspended?', values: yesNo },
            { property: 'isMedicalBoard', type: DataType.ToggleYesNo, value: this.state.data.isMedicalBoard, label: 'NKR Medical Board Center?', values: yesNo },
            { property: 'isAllIn', type: DataType.ToggleYesNoUnknown, value: this.state.data.isAllIn, label: 'Equal Access Center?', values: yesNoUnknown },
            { property: 'isAdpPartner', type: DataType.ToggleYesNo, value: this.state.data.isAdpPartner, label: 'Is Partner Center?', values: yesNo },
            { property: 'isDcnParticipant', type: DataType.ToggleYesNo, value: this.state.data.isDcnParticipant, label: 'Is DCN Participant?', values: yesNo },
            { property: 'isFamilyVoucherParticipant', type: DataType.ToggleYesNo, value: this.state.data.isFamilyVoucherParticipant, label: 'Is Family Voucher Participant?', values: yesNo },
            { property: 'isMicrositeParticipant', type: DataType.ToggleYesNo, value: this.state.data.isMicrositeParticipant, label: 'Is Microsite Participant?', values: yesNo },
            { property: 'isMicrositeReferralCenter', type: DataType.ToggleYesNo, value: this.state.data.isMicrositeReferralCenter, label: <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}><Label bsStyle='warning' style={{ lineHeight: '15px' }}>Only Owner Should Change</Label>&nbsp;Accepts Microsite Referrals?</div>, values: yesNo },
            { property: 'useMicrositeResponsibilityConfirmation', type: DataType.ToggleYesNo, value: this.state.data.useMicrositeResponsibilityConfirmation, label: 'Use Microsite Responsibility Question?', values: yesNo },

            { property: 'isDppParticipant', type: DataType.ToggleYesNo, value: this.state.data.isDppParticipant, label: 'Donor Protection Program?', values: yesNo },
            { property: 'isStreamlinedBilling', type: DataType.ToggleYesNo, value: this.state.data.isStreamlinedBilling, label: 'Is Streamlined Billing Service?', values: yesNo },
            { property: 'isFollowUpParticipant', type: DataType.ToggleYesNo, value: this.state.data.isFollowUpParticipant, label: 'Is Follow-Up Participant?', values: yesNo },
            { property: 'dashType', type: DataType.ToggleOrganType, value: this.state.data.dashType, label: 'DASH Participant Type?', values: centerOrganType },
            { property: 'isLiverDonorShieldARCenter', type: DataType.ToggleYesNo, value: this.state.data.isLiverDonorShieldARCenter, label: 'Uses Liver Donor Shield AR?', values: yesNo },
            { property: 'isInDonorShield', type: DataType.ToggleYesNoPilot, value: this.state.data.isInDonorShield, label: 'In Donor Shield Program?', values: yesNoPilot },
            { property: 'hasRecipientShield', type: DataType.ToggleYesNo, value: this.state.data.hasRecipientShield, label: 'Recipient Shield Program?', values: yesNoPilot },
            { property: 'isGoldStar', type: DataType.ToggleYesNo, value: this.state.data.isGoldStar, label: 'Is Gold Star Center?', values: yesNo },
            { property: 'isDonorShieldDirectLiverCenter', type: DataType.ToggleYesNo, value: this.state.data.isDonorShieldDirectLiverCenter, label: 'Is Donor Shield Direct Liver Center?', values: yesNo },

            { property: 'isInListingService', type: DataType.ToggleYesNo, value: this.state.data.isInListingService, label: 'Center Listing Service?', values: yesNo },
            { property: 'isPrivate', type: DataType.ToggleYesNo, value: this.state.data.isPrivate, label: 'Private Center?', values: yesNo },

            { property: 'hasAgreedToFees', type: DataType.ToggleYesNo, value: this.state.data.hasAgreedToFees, label: 'Billable Center?', values: yesNo },
            { property: 'paysUsViaAch', type: DataType.ToggleYesNo, value: this.state.data.paysUsViaAch, label: 'Center Pays via ACH?', values: yesNo },
            { property: 'isBillableKidney', type: DataType.ToggleYesNo, value: this.state.data.isBillableKidney, label: 'Is Billable (Kidney)?', values: yesNo },
            { property: 'isBillableLiver', type: DataType.ToggleYesNo, value: this.state.data.isBillableLiver, label: 'Is Billable (Liver)?', values: yesNo },


            { property: 'isAcceptingReferrals', type: DataType.ToggleYesNo, value: this.state.data.isAcceptingReferrals, label: 'In NKR\'s DASH Referral Network?', values: yesNo },

            { property: 'isPatientReferralNetwork', type: DataType.ToggleYesNo, value: this.state.data.isPatientReferralNetwork, label: 'Is in Patient Referral Network?', values: yesNo },
            { property: 'isMicrositeCenterOfExcellence', type: DataType.ToggleYesNo, value: this.state.data.isMicrositeCenterOfExcellence, label: 'Microsite Center of Excellence?', values: yesNo },
            { property: 'isNutcrackerCenterOfExcellence', type: DataType.ToggleYesNo, value: this.state.data.isNutcrackerCenterOfExcellence, label: 'Nutcracker Center of Excellence?', values: yesNo },
            { property: 'isStrongBoxCenter', type: DataType.ToggleYesNo, value: this.state.data.isStrongBoxCenter, label: 'Is StrongBox Center?', values: yesNo },

            { property: 'nddReferrals', type: DataType.ToggleYesNo, value: this.state.data.nddReferrals, label: <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}><Label bsStyle='warning' style={{ lineHeight: '15px' }}>Legacy</Label>&nbsp;NDD Post-Medical-Cert Referrals?</div>, values: yesNo },

            { property: 'excludeDataCompliance', type: DataType.ToggleYesNo, value: this.state.data.excludeDataCompliance, label: 'Exclude from MCR?', values: yesNoUnknown },
            { property: 'isRdnParticipant', type: DataType.ToggleYesNo, value: this.state.data.isRdnParticipant, label: 'Is Remote Direct Network Participant?', values: yesNo },
            { property: 'isAccParticipant', type: DataType.ToggleYesNo, value: this.state.data.isAccParticipant, label: 'AC&C?', values: yesNo },
            { property: 'hasCenterBilling', type: DataType.ToggleYesNo, value: this.state.data.hasCenterBilling, label: 'Center Billing?', values: yesNo },
            { property: 'ccbReviewDays', type: DataType.Integer, value: this.state.data.ccbReviewDays, label: 'CCB Review Days:', values: _.range(7, 22) },
            { property: 'reduceEmails', type: DataType.ToggleYesNo, value: this.state.data.reduceEmails, label: 'Reduce Emails?', values: yesNo },
            { property: 'isUtilizing3gKidneyForLife', type: DataType.ToggleYesNo, value: this.state.data.isUtilizing3gKidneyForLife, label: 'Is Kidney for Life Center?', values: yesNo },

            { property: 'doesAntibodyScreening', type: DataType.ToggleYesNo, value: this.state.data.doesAntibodyScreening, label: 'Uses NKR Antibody Screening?', values: yesNo },

            { property: 'getsFreeCheekSwabs', type: DataType.ToggleYesNo, value: this.state.data.getsFreeCheekSwabs, label: 'Gets Free Cheek Swabs?', values: yesNo },

            { property: 'isExpertSystemParticipant', type: DataType.ToggleYesNo, value: this.state.data.isExpertSystemParticipant, label: <><Label bsStyle='warning'>Requires DASH</Label>&nbsp;<span>NKR Expert System Center?</span></>, values: yesNo },
            { property: 'doesGraftFailureQuestions', type: DataType.ToggleYesNo, value: this.state.data.doesGraftFailureQuestions, label: 'Expanded Rejection/Graft Failure data compliance?', values: yesNo },
            { property: 'betaActivationStatus', type: DataType.ToggleYesNoPilot, value: this.state.data.betaActivationStatus, label: 'Beta Test Status?', values: onOffPilot },


            { property: 'packagingAchApproved', type: DataType.ToggleYesNo, value: this.state.data.packagingAchApproved, label: 'Approved:', values: yesNo },
        ];

        let summary: IFormItem = {
            style: 'info',
            sectionName: 'Summary',
            fields: [
                'alias',
                'nkrLiaisonId',
                'executiveSponsorId'
            ]
        };

        let memberCenterAgreements: IFormItem = {
            style: 'info',
            sectionName: 'Member Center Agreements',
            fields: isNkr ? [
                'memberCenterType',
                'isSuspended',
                'isAllIn',
                'isAdpPartner',
                'isDppParticipant',
                'space',
                'space',
                'space'
            ] : [
                'memberCenterType',
                'isSuspended',
                'space',
                'space',
                'space',
                'space'
            ]
        };

        let productUsage: IFormItem = {
            style: 'success',
            sectionName: 'Product Usage',
            fields: isNkr ? [
                'dashDomain',
                'dashType',
                'isFamilyVoucherParticipant',
                'isMicrositeParticipant',
                'isFollowUpParticipant',
                'isInDonorShield',
                'doesAntibodyScreening'
            ] : [
                'dashDomain',
                'dashType',
                'isMicrositeParticipant',
                'isFollowUpParticipant',
                'isDonorShieldDirectLiverCenter',
                ...(showLiverDonorShieldAR ? ['isLiverDonorShieldARCenter'] : [])
            ]
        };

        let highPerformingCenter: IFormItem = {
            style: 'success',
            sectionName: 'High Performing Center?',
            fields: isNkr ? [
                'isDcnParticipant',
                'isUtilizing3gKidneyForLife',
                'isExpertSystemParticipant',
                'doesGraftFailureQuestions',
                'isMicrositeCenterOfExcellence',
                'isNutcrackerCenterOfExcellence',
                'isStrongBoxCenter',
                'isMedicalBoard',
            ] : [
                'isMicrositeCenterOfExcellence',
                'doesGraftFailureQuestions',
                'isMedicalBoard'
            ]
        };

        let billingSettings: IFormItem = {
            style: 'warning',
            sectionName: 'Billing Settings',
            fields: isNkr ? [
                'isBillableKidney',
                'isBillableLiver',
                'paysUsViaAch',
                'overrideDonorsProtectedKidney',
                'overrideDonorsProtectedLiver',
                'isInactivityFeeWaived',
                'rmcrIsContractFeeWaived',
                'monthlyFee',
                'dashFee',
                'dashNotes',
                'achRoutingNumber',
                'achAccountNumber'
            ] : [
                'isBillableKidney',
                'isBillableLiver',
                'paysUsViaAch',
                'overrideDonorsProtectedLiver',
                'isInactivityFeeWaived',
                'rmcrIsContractFeeWaived',
                'monthlyFee',
                'dashFee',
                'dashNotes',
                'achRoutingNumber',
                'achAccountNumber'
            ]
        };

        if (this.state.data.organPackaging == OrganPackaging.InHouse) {
            billingSettings = {
                ...billingSettings,
                subSection: {
                    name: 'Packaging Remittance',
                    fields: [
                        'packagingAchRoutingNumber',
                        'packagingAchAccountNumber',
                        'packagingAchApproved'
                    ]
                }
            };
        }

        let poSettings: IFormItem = {
            style: 'warning',
            sectionName: 'Purchase Orders',
            fields: [
                'invoicePoNumber',
                'invoicePoStartDate',
                'invoicePoExpirationDate',
                'poAmount',
                'poBalance']
        };

        let referralCenter: IFormItem = {
            style: 'warning',
            sectionName: 'Referral Center?',
            fields: [
                'isAcceptingReferrals',
                'isPatientReferralNetwork'
            ]
        };

        const locSettings: IFormItem = {
            style: 'warning',
            sectionName: ' Member Center Geographic Coordinates ',
            fields: [
                'latitude',
                'longitude',
                'geoCheck'
            ]
        };

        let agreementsAndContracts: IFormItem = {
            style: 'info',
            sectionName: 'Agreements & Contracts',
            fields: ['contracts']
        };

        let centerProfileRows: IFormItemRow[] = [
            {
                sections: [
                    summary,
                    memberCenterAgreements,
                ]
            },
            {
                sections: [
                    productUsage,
                    highPerformingCenter,
                ]
            },
            {
                sections: [
                    billingSettings,
                    poSettings,
                    referralCenter
                ]
            },
            {
                sections: [
                    agreementsAndContracts,
                    locSettings
                ]
            }
        ];

        return { nonToggleList, toggleList, centerProfileRows };
    }

    save = (reasonsByProperty?: Record<string, string>) => {
        let request: IUpdateAdminDataRequest = {
            ...this.state.data,
            changeReasonsJson: reasonsByProperty && Object.keys(reasonsByProperty).length > 0 ? JSON.stringify(reasonsByProperty) : undefined,
            files: this.state.data.files.filter(x => x.newFile != null).map(x => {
                return {
                    fileType: x.typeId,
                    fileVersion: x.newFile.documentsVersionId,
                    fileDate: x.newFile.date,
                    file: x.newFile.file
                }
            })
        };

        this.props.showLoading('Saving center profile admin data, one moment please...').then(() => {
            this.props.updateData(this.props.centerId, request)
                .then(response => {
                    if (response.data.success) {
                        this.setState({
                            data: response.data.results,
                            hash: hash(response.data.results)
                        }, () => {
                            toast.success('Center profile admin data updated successfully');
                            this.props.onDataChanged(false);
                        });
                    }
                }).finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    computeDirtyTrackedChanges = (): ITrackedChange[] => {
        const { trackedFields, data, originalData } = this.state;
        const toCamel = (s: string) => s.length > 0 ? s[0].toLowerCase() + s.slice(1) : s;
        const display = (v: any, isBool: boolean) => {
            if (v == null) return '';
            if (isBool) {
                if (v === true || v === 1 || v === '1') return 'Yes';
                if (v === false || v === 0 || v === '0') return 'No';
            }
            return String(v);
        };

        return trackedFields
            .map(tf => {
                const key = toCamel(tf.propertyName);
                const before = (originalData as any)[key];
                const after = (data as any)[key];
                if (before === after) return null;
                return { propertyName: tf.propertyName, columnLabel: tf.columnLabel, before: display(before, tf.isBool), after: display(after, tf.isBool) };
            })
            .filter((c): c is ITrackedChange => c !== null);
    }

    validateBeforeSave = async () => {
        let validation = await Validate.validateAdminData(this.state.data, this.props.validateAchRoutingNumber);
        let isValid = Validate.isAdminDataValid(validation);
        let errorList = Validate.getInvalidAdminDataItems(validation).filter(x => x.show);

        this.setState({
            validation,
            isValid,
            isDirty: true,
        }, () => {
            this.props.updateErrors(errorList);

            if (!this.state.isValid) {
                window.scrollTo(0, 0);
                return;
            }

            const dirtyTracked = this.computeDirtyTrackedChanges();
            if (dirtyTracked.length === 0) {
                this.save();
            } else {
                this.setState({ pendingTrackedChanges: dirtyTracked, showPerFieldReason: true });
            }
        });
    }

    handleSaveWithReasons = (reasonsByProperty: Record<string, string>) => {
        this.setState({ showPerFieldReason: false, pendingTrackedChanges: [] }, () => this.save(reasonsByProperty));
    }

    handleCancelPerFieldReason = () => {
        this.setState({ showPerFieldReason: false, pendingTrackedChanges: [] });
    }

    handleExport = () => {
        this.props.getActivityLog(this.props.centerId, {}).then(response => {
            if (!response.data.success) return;
            const rows: import('@nkr/store/center/actions-center-activity-log').IAuditFieldLog[] = response.data.results;
            const escape = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
            const lines = [
                ['Date/Time', 'Field', 'Before', 'After', 'By', 'Reason'].map(escape).join(','),
                ...rows.map(r => [
                    r.changeDateTime ? new Date(r.changeDateTime).toLocaleString() : '',
                    r.columnName,
                    r.beforeValue,
                    r.afterValue,
                    r.userFullName ?? r.userId,
                    r.reason
                ].map(escape).join(','))
            ];
            const blob = new Blob([lines.join('\r\n')], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `activity-log-center-${this.props.centerId}-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        });
    }

    handleOpenActivityLog = () => {
        this.setState({ showActivityLog: true });
    }

    handleCloseActivityLog = () => {
        this.setState({ showActivityLog: false });
    }

    handleFieldChange = (e: FormEvent<FormControl & HTMLInputElement & HTMLSelectElement>) => {
        let { name, value } = e.currentTarget;
        if (name == 'poBalance')
            return;

        //debugger;

        let data = _.cloneDeep(this.state.data);

        let { nonToggleList, toggleList } = this.getLists();

        let fullList = [...nonToggleList, ...toggleList];

        let p = fullList.find(x => x.property == name);

        if (p.type == DataType.String || p.type == DataType.Password || p.type == DataType.Fee || p.type == DataType.Select || p.type == DataType.Date || p.type == DataType.Float) {
            data[name] = value;
        } else if (p.type == DataType.Integer || p.type == DataType.SelectInteger) {
            data[name] = parseInt(value);
        }

        let newHash = hash(data);

        this.setState({
            data
        }, () => {
            this.props.onDataChanged(newHash != this.state.hash)
        });
    }

    handleToggleChange = (e: FormEvent<HTMLInputElement>) => {
        let { name, value } = e.currentTarget;
        let data = _.cloneDeep(this.state.data);

        let { toggleList } = this.getLists();

        let p = toggleList.find(x => x.property == name);

        data[name] = parseInt(value);

        let newHash = hash(data);

        this.setState({
            data
        }, () => {
            this.props.onDataChanged(newHash != this.state.hash)
        });
    }

    handleOrganTypeToggleChange = (e: FormEvent<HTMLInputElement>) => {
        let { name, value } = e.currentTarget;
        let data = _.cloneDeep(this.state.data);

        data[name] = value === '' ? null : parseInt(value);

        let newHash = hash(data);

        this.setState({
            data
        }, () => {
            this.props.onDataChanged(newHash != this.state.hash)
        });
    }

    handleFileChange = (fileEntityType: VersionedFileEntityType, file: File, documentsVersionId: number, date: string) => {
        var data = _.cloneDeep(this.state.data);

        let f = data.files.find(x => x.typeId == fileEntityType);

        f.newFile = {
            file,
            documentsVersionId,
            date
        };

        let newHash = hash(data);

        this.setState({
            data
        }, () => {
            this.props.onDataChanged(newHash != this.state.hash)
        });
    }

    handleFileRemove = (fileEntityType: VersionedFileEntityType) => {
        var data = _.cloneDeep(this.state.data);

        let f = data.files.find(x => x.typeId == fileEntityType);

        f.newFile = null;

        let newHash = hash(data);

        this.setState({
            data
        }, () => {
            this.props.onDataChanged(newHash != this.state.hash)
        });
    }

    handleFileDownload = (id: number) => {
        this.props.getVersionedFile(this.props.centerId, id).then(response => {
            downloadFile(response.data, `file.pdf`, response.headers.contentType);
        });
    }

    isTextItem = (item: ITextItem | IToggleItem): item is ITextItem => {
        return item !== undefined ? (item as ITextItem).validation !== undefined : false;
    }

    renderFields = (property: string, index: number, nonToggleList: ITextItem[], toggleList: IToggleItem[], subSection: boolean) => {
        let item = (nonToggleList.find(x => x.property === property) || toggleList.find(x => x.property === property)) as (ITextItem | IToggleItem);

        let contentSm = item.type == DataType.Modal ? 11 : 6;

        let disabled = item.disabled != null ? item.disabled : false;

        return (
            <FormGroup key={'text-' + index} validationState={(this.isTextItem(item) && item.validation) ? (!item.valid ? 'error' : null) : null}>
                {item.type != DataType.Modal &&
                    <Col xs={12} sm={6} componentClass={ControlLabel} style={{ fontWeight: subSection ? 'normal' : null }}>{item.label}</Col>
                }

                <Col xs={12} sm={contentSm}>
                    {item.type == DataType.String &&
                        <FormControl
                            name={item.property}
                            tabIndex={index + 1}
                            disabled={disabled}
                            value={item.value}
                            autoComplete={'new-data'}
                            onChange={this.handleFieldChange}
                        />
                    }

                    {(item.type == DataType.Select || item.type == DataType.SelectInteger) &&
                        <FormControl
                            componentClass='select'
                            name={item.property}
                            tabIndex={index + 1}
                            disabled={disabled}
                            value={item.value}
                            onChange={this.handleFieldChange}
                        >
                            {item.values.map(listItem =>
                                <option value={listItem.id}>{listItem.description}</option>
                            )}
                        </FormControl>
                    }

                    {(item.type === DataType.ToggleYesNo || item.type === DataType.ToggleYesNoUnknown || item.type === DataType.ToggleYesNoPilot) &&
                        item.values.map((toggleItem, valueIndex) =>
                            <label key={'toggle-' + index + '-value-' + valueIndex} className="radio-inline">
                                <input
                                    id={item.property + '-' + valueIndex}
                                    type="radio"
                                    disabled={disabled}
                                    name={item.property}
                                    value={toggleItem.value}
                                    checked={item.value === toggleItem.value}
                                    onChange={this.handleToggleChange}
                                /> {toggleItem.label}
                            </label>
                        )
                    }

                    {item.type === DataType.ToggleOrganType &&
                        item.values.map((toggleItem, valueIndex) =>
                            <label key={'toggle-' + index + '-value-' + valueIndex} className="radio-inline">
                                <input
                                    id={item.property + '-' + valueIndex}
                                    type="radio"
                                    disabled={disabled}
                                    name={item.property}
                                    value={toggleItem.value == null ? '' : toggleItem.value}
                                    checked={item.value === toggleItem.value}
                                    onChange={this.handleOrganTypeToggleChange}
                                /> {toggleItem.label}
                            </label>
                        )
                    }

                    {item.type == DataType.Fee &&
                        <InputGroup>
                            <InputGroup.Addon>$</InputGroup.Addon>
                            <FormControl
                                name={item.property}
                                tabIndex={index + 1}
                                disabled={disabled}
                                value={item.value}
                                onChange={this.handleFieldChange}
                            />
                        </InputGroup>
                    }

                    {item.type == DataType.Date &&
                        <FormControl
                            type='date'
                            name={item.property}
                            disabled={disabled}
                            tabIndex={index + 1}
                            min='0001-01-01'
                            max='9999-01-01'
                            value={item.value}
                            onChange={this.handleFieldChange}
                        />
                    }

                    {item.type == DataType.Float &&
                        <InputGroup>
                            <FormControl
                                name={item.property}
                                tabIndex={index + 1}
                                value={item.value}
                                onChange={this.handleFieldChange}
                            />
                        </InputGroup>
                    }

                    {item.type == DataType.Component &&
                        item.value
                    }

                    {item.type == DataType.Modal && this.state.data.files.map(item => {
                        let dvList = this.props.documentVersionList.filter(x => x.entityType == item.typeId);

                        return (
                            <UploadFile
                                fileEntityType={item.typeId}
                                modalTitle={item.type}
                                valid={true}
                                withVersionHistory={true}
                                labelText={item.type}
                                disable={false}
                                documentsVersions={dvList}
                                type={UploadFileType.WithVersionControl}
                                newFileDisplayType={NewFileDisplayType.AdminData}
                                newFile={item.newFile}
                                versionedFiles={item.files}
                                onSelectFile={this.handleFileChange}
                                onRemoveFile={this.handleFileRemove}
                                onDownloadFile={this.handleFileDownload}
                            />
                        );
                    })}
                </Col>
            </FormGroup>
        )
    }

    render = () => {
        const { nonToggleList, toggleList, centerProfileRows } = this.getLists();

        return (
            <>
                <CenterProfileNav
                    activeKey={profileNavKey.ADMIN}
                    isAdmin={this.props.isAdmin}
                    isAdminUser={this.props.isAdminUser}
                    centerId={this.props.centerId}
                    hasChanges={this.props.hasDataChanges}
                    onTabClick={this.props.onTabClick}
                    onSave={this.validateBeforeSave}
                    onActivityLog={this.handleOpenActivityLog}
                    onExport={this.handleExport}
                />

                <div style={{ padding: 25, border: '1px solid #ddd', borderRadius: 4 }}>
                    {centerProfileRows.map((row, rowIndex) =>
                        <Row key={'row-' + rowIndex}>
                            {row.sections.map((section, sectionIndex) =>
                                <Col key={'section-' + sectionIndex} xs={12} lg={6}>
                                    <Panel bsStyle={section.style}>
                                        <Panel.Heading>{section.sectionName}</Panel.Heading>
                                        <Panel.Body>
                                            <Form horizontal>
                                                {section.fields.map((val, index) => this.renderFields(val, index, nonToggleList, toggleList, false))}

                                                {section.subSection != null && (
                                                    <>
                                                        <FormGroup>
                                                            <Col xs={12} sm={6} componentClass={ControlLabel}>{section.subSection.name}</Col>
                                                        </FormGroup>

                                                        {section.subSection.fields.map((val, index) => this.renderFields(val, index, nonToggleList, toggleList, true))}
                                                    </>
                                                )}
                                            </Form>
                                        </Panel.Body>
                                    </Panel>
                                </Col>
                            )}
                        </Row>
                    )}

                    {this.state.data.nkrBillingComments != '' && (
                        <div className='row center-admin'>
                            <div className='col-xs-12 col-sm-12'>
                                <Well bsSize='sm'>
                                    {ReactHtmlParser(this.state.data.nkrBillingComments)}
                                </Well>
                            </div>
                        </div>
                    )}
                </div>

                <PerFieldReasonModal
                    show={this.state.showPerFieldReason}
                    changes={this.state.pendingTrackedChanges}
                    onConfirm={this.handleSaveWithReasons}
                    onClose={this.handleCancelPerFieldReason}
                />

                <ActivityLogModal
                    show={this.state.showActivityLog}
                    centerId={this.props.centerId}
                    onClose={this.handleCloseActivityLog}
                    getActivityLog={this.props.getActivityLog}
                />
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        app: appState.app
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getData: (centerId) => {
            return dispatch(CenterActionCreators.getAdminData(centerId));
        },
        updateData: (centerId, request) => {
            return dispatch(CenterActionCreators.updateAdminData(centerId, request));
        },
        getVersionedFile: (centerId, id) => {
            return dispatch(CenterActionCreators.getVersionedFile(centerId, id));
        },
        validateAchRoutingNumber: (achRoutingNumber) => {
            return dispatch(BillingActionCreators.validateAchRoutingNumber(achRoutingNumber));
        },
        getActivityLog: (centerId, filters) => {
            return dispatch(ActivityLogActionCreators.getActivityLog(centerId, filters));
        },
        getActivityLogTrackedFields: () => {
            return dispatch(ActivityLogActionCreators.getActivityLogTrackedFields());
        }
    }
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(CenterAdminTab);